import React, { useEffect, useRef, useState } from 'react';
import { exchangeDate, notify } from "../../helpers";
import Spacing from '../Spacing';
import { Drawer } from 'vaul';
import { checkClaim } from '../../services/npiManagement';
import Skeleton from 'react-loading-skeleton';
import { Copy, FolderSync, Info, RotateCcw, X } from 'lucide-react';

const grid = (cols) => ({
  gridTemplateColumns: cols || "repeat(auto-fit, minmax(140px, 1fr))",
  alignItems: "start",
});

export default function ClaimSingleViewModal(props) {
  const {
    patient_data = {},
    activeModal = false,
    setActiveModal = () => {},
    previousScrollPosition = 0,
    claimSearchData = {},
    checkClaims = true,
  } = props;

  const [claimDates, setClaimDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [claimData, setClaimData] = useState({});
  const [claimDataLoader, setClaimDataLoader] = useState(true);
  const [claimErrorLog, setClaimErrorLog] = useState({});
  const [claimPatientInfo, setClaimPatientInfo] = useState({});
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (activeModal && checkClaims) {
      let dos = patient_data?.dos || "";
      if (typeof dos === "string" && dos.includes("T")) {
        dos = dos.split("T")[0];
      }
      loadClaimData(dos);
    } else {
      setClaimDates(claimSearchData?.all_dos || []);
      setClaimPatientInfo(claimSearchData?.patient_info || {});
      if(claimSearchData?.status === 'success') {
        setClaimData(claimSearchData?.result);
      } else {
        setClaimErrorLog(claimSearchData);
      }
      setClaimDataLoader(false);
      setSelectedDate(claimSearchData?.dos || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal]);

  const loadClaimData = async (dateISO = "", setDate = true, recheck = false) => {
    const syncData = {
      account_id: patient_data?.account_id,
      location_id: patient_data?.location_id,
      patient_id: checkClaims ? patient_data?.patient_id : patient_data?.id,
      waiting_room_id: (checkClaims && setDate) ? patient_data?.id : null,
      start_date: dateISO || "",
      end_date: dateISO || "",
      recheck: recheck || false,
    }
    setClaimDataLoader(true);
    try {
      const response = await checkClaim(syncData);
      if(response){
        if(response?.detail) {
          setClaimErrorLog(response);
        } else {
          if(response?.data?.status === "success"){
            if(response?.data?.all_dos?.length === 0 && (!response?.data?.result || Object.keys(response?.data?.result)?.length === 0) && (!response?.data?.raw_request || Object.keys(response?.data?.raw_request)?.length === 0)) {
              handleDrawerClose();
              return;
            }
            setClaimDates(response?.data?.all_dos);
            setClaimPatientInfo(response?.data?.patient_info || {});
            setClaimData(response?.data?.result);
            if(setDate) {
              setSelectedDate(response?.data?.dos || "");
            }
            setClaimErrorLog({});
          } else {
            setClaimDates(response?.data?.all_dos);
            setClaimPatientInfo(response?.data?.patient_info || {});
            if(setDate) {
              setSelectedDate(response?.data?.dos || "");
            }
            setClaimErrorLog(response?.data);
          }
        }
      }
    } catch (error) {
      setClaimErrorLog(error);
    } finally {
      setClaimDataLoader(false);
    }
  };

  const formatDateAsMDY = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string" || dateStr?.length !== 8) return "-";
    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6);
    const day = dateStr.slice(6, 8);
    return `${month}/${day}/${year}`;
  };

  const handleDateClick = (dateISO) => {
    if (!dateISO) return;
    setSelectedDate(dateISO);
    loadClaimData(dateISO,false);
  };

  const handleCopy = async () => {
    try {
      const dataToCopy = claimErrorLog?.raw_request || claimErrorLog;
      const content = JSON.stringify(dataToCopy ?? {}, null, 2);
      await navigator.clipboard.writeText(content);
      notify("success", "Logs copied to clipboard!", 0.5);
    } catch (err) {
      console.error("Failed to copy logs:", err);
      notify("error", "Failed to copy logs!", 0.5);
    }
  };

  const handleDrawerClose = () => {
    setActiveModal(false);
    window.scrollTo(0, previousScrollPosition);
  };

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={handleDrawerClose} dismissible={false}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn">
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4" onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <Drawer.Title className="sr-only">View Claim</Drawer.Title>
            <Drawer.Description className="sr-only">View Individual Claim</Drawer.Description>
            <div className="docpro_modal_wrap docpro_style_1 docpro_modal_lg">
              <div className="docpro_modal">
                <button
                  className="docpro_close_btn"
                  onClick={() => handleDrawerClose()}
                >
                  <X />
                </button>

                {claimDataLoader ? (
                  <>
                    <h2 className="docpro_fs_24 docpro_mb_0 text-center">Claim Details</h2>
                    <Spacing lg="10" sm="25" />

                    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8 }}>
                      <h3 className="docpro_section_title">Claim status</h3>
                      <div className="docpro_section_body">
                        <div className="docpro_grid_flex_wrap">
                          <Skeleton width="6rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                        </div>
                        <Spacing lg="15" sm="20" />
                        <div className="docpro_grid_flex_wrap">
                          <Skeleton width="7rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="3rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="3rem" borderRadius="8px"></Skeleton>
                        </div>
                      </div>

                      <h3 className="docpro_section_title docpro_section_divider">Service details</h3>
                      <div className="docpro_section_body">
                        <div className="docpro_grid docpro_grid_gap_10" style={{ ...grid("1fr 0.7fr 0.9fr 1.6fr 1.8fr"), paddingBottom: 5, borderBottom: "1px solid #f3f4f6" }}>
                          <div>Entity Name</div>
                          <div>Middle Name</div>
                          <div>Last Name</div>
                          <div>Date Information</div>
                          <div>Services</div>
                        </div>
                        <div className="docpro_grid docpro_grid_gap_10" style={{ ...grid("1fr 0.7fr 0.9fr 1.6fr 1.8fr"), padding: "10px 0" }}>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="3rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4.5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                        </div>
                        <div className="docpro_grid docpro_grid_gap_10" style={{ ...grid("1fr 0.7fr 0.9fr 1.6fr 1.8fr"), padding: "10px 0", borderTop: "1px solid #f3f4f6" }}>
                          <Skeleton width="5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="3.5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4.5rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="4rem" borderRadius="8px"></Skeleton>
                          <Skeleton width="6rem" borderRadius="8px"></Skeleton>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="docpro_fs_24 docpro_mb_0 text-center">Claim Details</h2>
                    <Spacing lg="10" sm="25" />

                    <div className="docpro_grid" style={{ gridTemplateColumns: (claimDates?.length > 0 && !claimDataLoader) ? "110px 1fr" : "auto", alignItems: "start" }}>
                      {/* LEFT: Date tabs */}
                      {claimDates?.length > 0 && !claimDataLoader && (
                        <div className="docpro_card_soft">
                          <div className="docpro_fw_600 docpro_primary_color" style={{ marginBottom: 8 }}>
                            Dates
                          </div>
                          <div className="docpro_grid_gap_8 docpro_claim_dates">
                            {(claimDates || []).map((d) => (
                              <button
                                key={d}
                                type="button"
                                onClick={() => handleDateClick(d)}
                                className={`docpro_claim_tab_btn ${selectedDate === d ? 'is-active' : ''}`}
                              >
                                {exchangeDate(d)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div>
                        {Object.keys(claimErrorLog)?.length > 0 ?
                          <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, minHeight: '80vh' }}>
                            <h3 className="docpro_section_title">Patient Info ({exchangeDate(selectedDate)})</h3>
                            <div className="docpro_section_body">
                              <div className="docpro_grid_flex_wrap">
                                <div>
                                  <div className="docpro_section_label">Last name</div>
                                  <div className="docpro_section_value">{claimPatientInfo?.last_name || "-"}</div>
                                </div>
                                <div>
                                  <div className="docpro_section_label">First name</div>
                                  <div className="docpro_section_value">{claimPatientInfo?.first_name || "-"}</div>
                                </div>
                                <div>
                                  <div className="docpro_section_label">DOB</div>
                                  <div className="docpro_section_value">{claimPatientInfo?.dob ? exchangeDate(claimPatientInfo?.dob) : "-"}</div>
                                </div>
                                <div>
                                  <div className="docpro_section_label">Insurance name</div>
                                  <div className="docpro_section_value">{claimPatientInfo?.insurance_name || "-"}</div>
                                </div>
                                <div>
                                  <div className="docpro_section_label">Insurance ID</div>
                                  <div className="docpro_section_value">{claimPatientInfo?.insurance_code || "-"}</div>
                                </div>
                              </div>
                            </div>
                            <Spacing lg="15" sm="20" />
                            <p className="docpro_section_label text-center docpro_red_color docpro_claim_error_message">{
                              claimErrorLog?.detail 
                              ? claimErrorLog?.detail 
                              : claimErrorLog?.message 
                              ? claimErrorLog?.message 
                              : claimErrorLog?.response && claimErrorLog?.response?.data && claimErrorLog?.response?.data?.detail 
                              ? claimErrorLog?.response?.data?.detail 
                              : ""
                            }</p>
                            <Spacing lg="15" sm="25" />
                            <div className="flex items-center docpro_grid_gap_10" style={{ justifyContent: "center" }}>
                              <button
                                className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                                style={{ padding: "12px 25px" }}
                                onClick={() => { handleCopy(); }}
                              >
                                <Copy width={18} height={18} />
                                <span>Copy Logs</span>
                              </button>
                              <button
                                className="docpro_bordered_btn docpro_fw_600 docpro_primary_color docpro_filed_btn"
                                style={{ padding: "12px 25px" }}
                                onClick={() => { loadClaimData(selectedDate, false, true); }}
                              >
                                <RotateCcw width={18} height={18} />
                                <span>Retry</span>
                              </button>
                            </div>
                          </div>
                        :
                        <>
                        <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, position: "relative" }}>
                          {claimData?.claim_status === 'denied' && (
                            <button
                              className="docpro_bordered_btn docpro_fw_600 docpro_primary_color docpro_filed_btn"
                              style={{ padding: "8px 20px", position: "absolute", right: 0, top: "-39px" }}
                              onClick={() => { loadClaimData(selectedDate, false, true); }}
                            >
                              <FolderSync width={20} height={20} />
                              <span>Check Resubmit Status</span>
                            </button>
                          )}
                          {/* ==================== Subscriber ==================== */}
                          <div className="docpro_section_body">
                            <Spacing lg="15" sm="15" />
                            <div className='docpro_grid_flex_wrap' style={{ flexFlow: 'row nowrap', alignItems: 'start' }}>
                              <div className="docpro_section_value" style={{ whiteSpace: 'nowrap' }}>Status category code</div>
                              <kbd className="docpro_kbd">{claimData?.stedi_claim_status?.claims?.[0]?.claimStatus?.statusCategoryCode || "-"}</kbd>
                              <div className="docpro_section_value">{claimData?.stedi_claim_status?.claims?.[0]?.claimStatus?.statusCategoryCodeValue || "-"}</div>
                            </div>
                            <Spacing lg="10" sm="10" />
                            <div className='docpro_grid_flex_wrap'>
                              <div className="docpro_section_value" style={{ whiteSpace: 'nowrap', minWidth: '140px' }}>Status code</div>
                              <div className="docpro_section_value" style={{ minWidth: '26px' }}>{claimData?.stedi_claim_status?.claims?.[0]?.claimStatus?.statusCode || "-"}</div>
                              <div className="docpro_section_value">{claimData?.stedi_claim_status?.claims?.[0]?.claimStatus?.statusCodeValue || "-"}</div>
                            </div>
                          </div>

                          <h3 className="docpro_section_title docpro_section_divider">Patient Info ({exchangeDate(selectedDate)})</h3>
                          <div className="docpro_section_body">
                            <div className="docpro_grid_flex_wrap">
                              <div>
                                <div className="docpro_section_label">Last name</div>
                                <div className="docpro_section_value">{claimPatientInfo?.last_name || "-"}</div>
                              </div>
                              <div>
                                <div className="docpro_section_label">First name</div>
                                <div className="docpro_section_value">{claimPatientInfo?.first_name || "-"}</div>
                              </div>
                              <div>
                                <div className="docpro_section_label">DOB</div>
                                <div className="docpro_section_value">{claimPatientInfo?.dob ? exchangeDate(claimPatientInfo?.dob) : "-"}</div>
                              </div>
                              <div>
                                <div className="docpro_section_label">Insurance name</div>
                                <div className="docpro_section_value">{claimPatientInfo?.insurance_name || "-"}</div>
                              </div>
                              <div>
                                <div className="docpro_section_label">Insurance ID</div>
                                <div className="docpro_section_value">{claimPatientInfo?.insurance_code || "-"}</div>
                              </div>
                            </div>
                          </div>

                          <h3 className="docpro_section_title docpro_section_divider">Claim status</h3>
                          <div className="docpro_section_body">
                            <div className="docpro_grid_flex_wrap">
                              <div>
                                <div className="docpro_section_label">Patient control number</div>
                                <div className="docpro_section_value">{claimData?.stedi_claim_status?.claims?.[0]?.claimStatus?.patientAccountNumber || "-"}</div>
                              </div>
                              <div>
                                <div className="docpro_section_label">Claim status tracking number</div>
                                <div className="docpro_section_value">{claimData?.stedi_claim_status?.claims?.[0]?.claimStatus?.trackingNumber || "-"}</div>
                              </div>
                              <div>
                                <div className="docpro_section_label">Payer claim control number</div>
                                <div className="docpro_section_value">{claimData?.stedi_claim_status?.claims?.[0]?.claimStatus?.tradingPartnerClaimNumber || "-"}</div>
                              </div>
                              <div>
                                <div className="docpro_section_label">Trading partner service ID</div>
                                <div className="docpro_section_value">{claimData?.stedi_claim_status?.tradingPartnerServiceId || "-"}</div>
                              </div>
                              <div>
                                <div className="docpro_section_label">Amount paid</div>
                                <div className="docpro_section_value">${claimData?.stedi_claim_status?.claims?.[0]?.claimStatus?.amountPaid ? `${claimData?.stedi_claim_status?.claims?.[0]?.claimStatus?.amountPaid} on ${exchangeDate(claimData?.stedi_claim_status?.claims?.[0]?.claimStatus?.paidDate)}` : 0}</div>
                              </div>
                              <div>
                                <div className="docpro_section_label">Submitted amount</div>
                                <div className="docpro_section_value">${claimData?.stedi_claim_status?.claims?.[0]?.claimStatus?.submittedAmount || 0}</div>
                              </div>
                              <div>
                                <div className="docpro_section_label">Effective date</div>
                                <div className="docpro_section_value">{exchangeDate(claimData?.stedi_claim_status?.claims?.[0]?.claimStatus?.effectiveDate) || "-"}</div>
                              </div>
                              <div>
                                <div className="docpro_section_label">Claim service dates</div>
                                <div className="docpro_section_value">{formatDateAsMDY(claimData?.stedi_claim_status?.claims?.[0]?.claimStatus?.claimServiceDate) || "-"}</div>
                              </div>
                              <div>
                                <div className="docpro_section_label">Claim status</div>
                                <div className="docpro_section_value">
                                  {claimData?.claim_status ?
                                    claimData?.claim_status === 'accepted' ?
                                    <span className='docpro_badge docpro_badge_green'>Accepted</span>
                                    : claimData?.claim_status === 'denied' ?
                                    <span className='docpro_badge docpro_badge_red'>Denied</span>
                                    : <span className='docpro_badge docpro_badge_grey'>{claimData?.claim_status}</span>
                                  : "-"}
                                </div>
                              </div>
                            </div>
                          </div>

                          <h3 className="docpro_section_title docpro_section_divider">Service details</h3>
                          <div className="docpro_section_body docpro_claim_table">
                            {/* header row */}
                            <div className="docpro_grid docpro_grid_gap_10" style={{ ...grid("0.7fr 0.8fr 1fr 0.8fr 1.2fr 0.7fr"), paddingBottom: 5, borderBottom: "1px solid #f3f4f6" }}>
                              <div style={{ minWidth: '80px' }}>Service</div>
                              <div style={{ minWidth: '80px' }}>Amount paid</div>
                              <div style={{ minWidth: '80px' }}>Submitted amount</div>
                              <div style={{ minWidth: '80px' }}>Effective date</div>
                              <div style={{ minWidth: '80px' }}>Status category code</div>
                              <div style={{ minWidth: '80px' }}>Status code</div>
                            </div>

                            {/* rows */}
                            {(() => {
                              const formatUSD = (v) => Number(v ?? 0).toLocaleString(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 2 });

                              const badgeClass = (cat) => 
                                cat === "F0" ? "docpro_badge docpro_badge_green" : 
                                cat === "F1" ? "docpro_badge docpro_badge_green" : 
                                cat === "F2" ? "docpro_badge docpro_badge_red" : "docpro_badge";

                              const details = claimData?.stedi_claim_status?.claims?.[0]?.serviceDetails || [];

                              const rows = details.flatMap((sd, idx) => {
                                const svc = sd?.service || {};
                                const statuses = Array.isArray(sd?.status) && sd.status?.length ? sd.status : [null];

                                const code = svc?.procedureId || "-";
                                const amountPaid = formatUSD(svc?.amountPaid);
                                const submittedAmount = formatUSD(svc?.submittedAmount);

                                return statuses.flatMap((st, j) => {
                                  const isFirst = j === 0;
                                  const row = [];

                                  if (!isFirst) {
                                    row.push(
                                      <div
                                        key={`sep-${idx}-${j}`}
                                        className="docpro_grid docpro_grid_gap_10"
                                        style={{
                                          gridTemplateColumns: "1fr 0.8fr 1fr 0.8fr 1fr 0.7fr",
                                          padding: 0,
                                        }}
                                      >
                                        <div style={{ gridColumn: "1 / 4" }} />
                                        <div style={{ gridColumn: "4 / 7", borderTop: "1px dashed #f3f4f6" }} />
                                      </div>
                                    );
                                  }

                                  row.push(
                                    <div
                                      key={`svc-${idx}-${j}`}
                                      className="docpro_grid docpro_grid_gap_10"
                                      style={{
                                        gridTemplateColumns: "0.7fr 0.8fr 1fr 0.8fr 1.2fr 0.7fr",
                                        padding: "6px 0",
                                        borderTop: isFirst && idx > 0 ? "1px solid #f3f4f6" : "none",
                                      }}
                                    >
                                      <div className="docpro_section_value">
                                        {isFirst ? (
                                          <div style={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: '80px' }}>
                                            {code}
                                          </div>
                                        ) : ( <span style={{ color: "transparent", display: 'inline-flex', minWidth: '80px' }}>.</span> )}
                                      </div>

                                      <div className="docpro_section_value" style={{ whiteSpace: 'nowrap', minWidth: '80px' }}>
                                        {isFirst ? amountPaid : <span style={{ color: "transparent" }}>.</span>}
                                      </div>

                                      <div className="docpro_section_value" style={{ whiteSpace: 'nowrap', minWidth: '80px' }}>
                                        {isFirst ? submittedAmount : <span style={{ color: "transparent" }}>.</span>}
                                      </div>

                                      <div className="docpro_section_value" style={{ whiteSpace: 'nowrap', minWidth: '80px' }}>{st ? exchangeDate(st.effectiveDate) : "-"}</div>

                                      <div className="docpro_section_value" style={{ whiteSpace: 'nowrap', minWidth: '80px' }}>
                                        {st?.statusCategoryCode ? (
                                          <span title={st?.statusCategoryCodeValue || ""} className={badgeClass(st.statusCategoryCode)}>
                                            {st.statusCategoryCode}
                                          </span>
                                        ) : ( "-" )}
                                      </div>

                                      <div className="docpro_section_value flex items-center docpro_grid_gap_4" style={{ whiteSpace: 'nowrap' }}>
                                        {st?.statusCode ? st.statusCode : "-"}
                                        <button className="docpro_icon_btn overview" style={{ width: 'auto', height: 'auto', borderRadius: '50%' }} >
                                          <Info width={15} height={15} />
                                          <div className="docpro_tip" style={{ whiteSpace: 'pre-wrap', width: '150px' }}>
                                            <span>{st?.statusCodeValue || ""}</span>
                                          </div>
                                        </button>
                                      </div>
                                    </div>
                                  );

                                  return row;
                                });
                              });

                              return rows?.length ? rows : <div style={{ padding: "10px 0", color: "#6b7280" }}>No service details found.</div>;
                            })()}
                          </div>
                        </div>
                        {claimData?.dos && (
                          <>
                            <Spacing lg="10" sm="20" />
                            <div className="docpro_section_value text-center">DOS: {exchangeDate(claimData?.dos)}</div>
                          </>
                        )}
                        </>
                        }
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}